import { useEffect, useMemo, useState } from 'react';
import { formatTime, toBerlinDateKey } from '@brimo/shared';
import { Banner, Skeleton } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { usePermissions } from '../../../lib/session';
import { bookAppointment, useEventTypes } from '../api';
import { slotDay, todayKey } from '../format';
import type { BookAppointmentBody, ConflictDetails, EventType, Slot } from '../types';
import { EditorCard, PrimaryAction } from './EditorCard';
import { ChipGroup, EditorField } from './fields';
import { referencesOf, type EditorProps } from './shared';
import { SlotPicker, monthOf, slotKey, slotSummary } from './SlotPicker';
import styles from './editor.module.css';

type Channel = EventType['channel'];

const CHANNELS: ReadonlyArray<{ value: Channel; label: string }> = [
  { value: 'video', label: 'Videocall' },
  { value: 'google_meet', label: 'Google Meet' },
  { value: 'teams', label: 'Microsoft Teams' },
  { value: 'zoom', label: 'Zoom' },
  { value: 'phone', label: 'Telefon' },
  { value: 'on_site', label: 'Vor Ort' },
  { value: 'property_address', label: 'Objektadresse' },
];

function conflictDetailsOf(error: unknown): ConflictDetails | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const details = error.details;
  if (!details || !Array.isArray(details['alternatives']) || !Array.isArray(details['conflicts']))
    return null;
  return details as unknown as ConflictDetails;
}

/** „Kalender J. Weiler" / „Round-Robin · 3 Berater" — whose calendar the slots come from. */
function calendarLabel(type: EventType | null): string {
  if (!type) return '';
  const members = type.members.filter((member) => member.active);
  if (type.owner && members.length <= 1) return `Kalender ${type.owner.shortName}`;
  if (members.length === 1 && members[0]) return `Kalender ${members[0].shortName}`;
  if (members.length > 1) return `Round-Robin · ${members.length} Berater`;
  return 'Kalender';
}

/** Kontaktakte II.E4 „Termin buchen": event type, reference, free slots from the calendar, channel → `POST /appointments`. */
export function AppointmentEditor({ record, task, onClose, onDone }: EditorProps) {
  const permissions = usePermissions();
  const types = useEventTypes(true);
  const active = useMemo(() => (types.data?.items ?? []).filter((type) => type.active), [types.data]);
  const [typeId, setTypeId] = useState<string | null>(null);
  const type = active.find((item) => item.id === typeId) ?? null;
  const references = useMemo(() => referencesOf(record), [record]);
  const [reference, setReference] = useState<string>(references[0]?.value ?? 'contact');
  const [month, setMonth] = useState(() => monthOf(todayKey()));
  const [day, setDay] = useState<string | null>(null);
  const [slot, setSlot] = useState<Slot | null>(null);
  const [channel, setChannel] = useState<Channel | null>(null);
  const [location, setLocation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<ConflictDetails | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (typeId !== null || active.length === 0) return;
    const preferred = active.find((item) => item.systemKey === 'consultation') ?? active[0] ?? null;
    if (preferred) setTypeId(preferred.id);
  }, [active, typeId]);

  useEffect(() => {
    if (!type) return;
    setChannel(type.channel);
    setLocation(type.locationText ?? '');
    setSlot(null);
    setDay(null);
    setConflict(null);
  }, [type]);

  const isAdmin = permissions.is('settings.organization', 'yes');
  const chosenReference = references.find((item) => item.value === reference);

  function pickSlot(next: Slot) {
    const key = toBerlinDateKey(new Date(next.startsAt));
    setMonth(monthOf(key));
    setDay(key);
    setSlot(next);
    setError(null);
    setConflict(null);
  }

  async function book(override: boolean) {
    if (!type) return;
    if (!slot) {
      setError('Bitte einen freien Slot wählen.');
      return;
    }
    const body: BookAppointmentBody = {
      eventTypeId: type.id,
      contactId: record.id,
      advisorId: slot.advisorId,
      startsAt: slot.startsAt,
    };
    if (channel) body.channel = channel;
    if ((channel === 'on_site' || channel === 'property_address') && location.trim())
      body.location = location.trim();
    if (chosenReference?.dealId) body.dealId = chosenReference.dealId;
    if (chosenReference?.ticketId) body.ticketId = chosenReference.ticketId;
    if (task) body.completesTaskId = task.id;
    if (override) body.overrideConflict = true;
    setPending(true);
    setError(null);
    try {
      const detail = await bookAppointment(body);
      onDone({
        toast: `Termin gebucht: ${detail.eventType.name}, ${slotDay(slot.startsAt)} ${formatTime(slot.startsAt)}.`,
      });
    } catch (cause) {
      const details = conflictDetailsOf(cause);
      if (details) setConflict(details);
      else if (cause instanceof ApiError && cause.status === 403)
        setError(cause.detailString('helper') ?? cause.message);
      else if (cause instanceof ApiError)
        setError(cause.fields['startsAt'] ?? cause.fields['dealId'] ?? cause.message);
      else setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  const subtitle = type
    ? `${type.name} · ${type.durationMinutes} Min · ${record.header.name}`
    : record.header.name;

  return (
    <EditorCard
      tone="ink"
      dot="var(--accent)"
      title="Termin buchen"
      subtitle={subtitle}
      label="Termin-Editor"
      onClose={onClose}
      primary={
        <PrimaryAction loading={pending} disabled={!type || types.isPending} onClick={() => void book(false)}>
          Buchen und bestätigen
        </PrimaryAction>
      }
    >
      {task ? <Banner tone="neutral">Die Buchung erledigt die Aufgabe „{task.title}“.</Banner> : null}
      {types.isError ? <Banner tone="danger">{errorMessage(types.error)}</Banner> : null}
      {!types.isPending && active.length === 0 ? (
        <Banner tone="warning">
          Es gibt noch keinen aktiven Ereignistyp. Legen Sie einen unter Beratung an.
        </Banner>
      ) : null}
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {conflict ? (
        <div className={styles.conflicts} role="alert">
          Terminkonflikt:{' '}
          {conflict.conflicts
            .map((item) => `${item.title} (${formatTime(item.startsAt)}–${formatTime(item.endsAt)})`)
            .join(', ')}
          {conflict.alternatives.length > 0 ? (
            <div className={styles.alternatives}>
              {conflict.alternatives.map((alternative) => (
                <button
                  key={slotKey(alternative)}
                  type="button"
                  className={styles.chip}
                  onClick={() => pickSlot(alternative)}
                >
                  {slotDay(alternative.startsAt)} {formatTime(alternative.startsAt)}
                </button>
              ))}
            </div>
          ) : null}
          {isAdmin ? (
            <button type="button" className={styles.ghostSmallInline} onClick={() => void book(true)}>
              Trotzdem anlegen
            </button>
          ) : null}
        </div>
      ) : null}
      <EditorField label="Art">
        {types.isPending ? (
          <Skeleton width={260} height={28} />
        ) : (
          <ChipGroup
            label="Art"
            options={active.map((item) => ({ value: item.id, label: item.name }))}
            value={typeId}
            onChange={setTypeId}
          />
        )}
      </EditorField>
      <EditorField label="Bezug" hint="bestimmt die Herkunft">
        <ChipGroup label="Bezug" options={references} value={reference} onChange={setReference} />
      </EditorField>
      <EditorField label="Freier Slot" hint={calendarLabel(type)}>
        <SlotPicker
          eventTypeId={typeId}
          month={month}
          onMonthChange={(next) => {
            setMonth(next);
            setDay(null);
          }}
          day={day}
          onDayChange={setDay}
          slot={slot}
          onSlotChange={pickSlot}
          emptyHint={
            type
              ? 'Keine freien Zeiten in diesem Monat. Der Ereignistyp braucht Berater mit Verfügbarkeit.'
              : 'Bitte zuerst eine Art wählen.'
          }
        />
        <div className={styles.pickerChosen} aria-live="polite">
          {slot ? (
            <>
              <span className={styles.pickerChosenLabel}>Gewählt</span>
              <span className={styles.pickerChosenValue}>{slotSummary(slot)}</span>
            </>
          ) : (
            <span className={styles.pickerChosenLabel}>Noch keine Zeit gewählt</span>
          )}
        </div>
      </EditorField>
      <EditorField label="Ort">
        <ChipGroup label="Ort" options={CHANNELS} value={channel} onChange={setChannel} />
        {channel === 'on_site' || channel === 'property_address' ? (
          <input
            className={styles.input}
            style={{ marginTop: 8 }}
            aria-label="Adresse"
            placeholder="Adresse"
            value={location}
            onChange={(event) => setLocation(event.target.value)}
          />
        ) : null}
      </EditorField>
    </EditorCard>
  );
}
